import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Num({ label, value, onChange, testId }) {
  return (
    <div><Label className="text-xs">{label}</Label>
      <Input type="number" min="1" value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} data-testid={testId} /></div>
  );
}

export function OfficeSettings({ open, onOpenChange }) {
  const [esc, setEsc] = useState(null);
  const [sla, setSla] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/office/escalation-settings"), api.get("/office/sla-settings")])
      .then(([e, s]) => { setEsc(e.data); setSla({ ...s.data, holidays: (s.data.holidays || []).join("\n") }); })
      .catch(() => toast.error("Could not load settings (admin only)"));
  }, [open]);

  const setEscBand = (band, k, v) => setEsc((p) => ({ ...p, [band]: { ...p[band], [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/office/escalation-settings", esc);
      await api.put("/office/sla-settings", {
        ...sla,
        holidays: sla.holidays.split("\n").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Settings saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="office-settings-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Office Settings</DialogTitle>
          <DialogDescription className="sr-only">Escalation timings and SLA working-hours configuration</DialogDescription></DialogHeader>
        {!esc || !sla ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="space-y-5">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Escalation timing (hours overdue before escalation)</p>
              <div className="grid grid-cols-2 gap-4">
                {[["critical_high", "Critical / High priority"], ["normal_low", "Normal / Low priority"]].map(([band, label]) => (
                  <div key={band} className="border border-border rounded-md p-3 space-y-2" data-testid={`esc-band-${band}`}>
                    <p className="text-xs font-semibold">{label}</p>
                    <Num label="→ Reporting manager" value={esc[band].manager_hours} onChange={(v) => setEscBand(band, "manager_hours", v)} testId={`esc-${band}-mgr`} />
                    <Num label="→ Department head" value={esc[band].head_hours} onChange={(v) => setEscBand(band, "head_hours", v)} testId={`esc-${band}-head`} />
                    <Num label="→ Management / MD" value={esc[band].md_hours} onChange={(v) => setEscBand(band, "md_hours", v)} testId={`esc-${band}-md`} />
                  </div>))}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">SLA — working hours</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Workday start</Label>
                  <Input type="time" value={sla.workday_start} onChange={(e) => setSla({ ...sla, workday_start: e.target.value })} data-testid="sla-workday-start" /></div>
                <div><Label className="text-xs">Workday end</Label>
                  <Input type="time" value={sla.workday_end} onChange={(e) => setSla({ ...sla, workday_end: e.target.value })} data-testid="sla-workday-end" /></div>
                <div className="col-span-2"><Label className="text-xs">Workdays</Label>
                  <div className="flex gap-2 flex-wrap pt-1.5">
                    {WEEKDAYS.map((d, i) => (
                      <label key={d} className="flex items-center gap-1 text-xs">
                        <input type="checkbox" className="w-3.5 h-3.5 accent-primary" checked={sla.workdays.includes(i)}
                          onChange={() => setSla({ ...sla, workdays: sla.workdays.includes(i) ? sla.workdays.filter((x) => x !== i) : [...sla.workdays, i].sort() })}
                          data-testid={`sla-workday-${i}`} />{d}
                      </label>))}
                  </div></div>
              </div>
              <div className="mt-3"><Label className="text-xs">Holidays (YYYY-MM-DD, one per line)</Label>
                <textarea rows={3} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  value={sla.holidays} onChange={(e) => setSla({ ...sla, holidays: e.target.value })} data-testid="sla-holidays" /></div>
              <label className="flex items-center gap-2 text-xs mt-2">
                <input type="checkbox" className="w-4 h-4 accent-primary" checked={!!sla.pause_on_hold}
                  onChange={(e) => setSla({ ...sla, pause_on_hold: e.target.checked })} data-testid="sla-pause-on-hold" />
                Pause SLA while a task is On Hold
              </label>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">SLA thresholds (business hours from creation)</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {["critical", "high", "normal", "low"].map((p) => (
                  <Num key={p} label={`${p[0].toUpperCase()}${p.slice(1)}`} value={sla.thresholds[p]}
                    onChange={(v) => setSla({ ...sla, thresholds: { ...sla.thresholds, [p]: v } })} testId={`sla-th-${p}`} />))}
              </div>
            </div>
            <Button className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-bold" disabled={busy} onClick={save} data-testid="office-settings-save">
              SAVE SETTINGS
            </Button>
          </div>)}
      </DialogContent>
    </Dialog>
  );
}
